/**
 * Auth service API client for the admin UI.
 * All calls go through the auth-service Worker.
 *
 * Hierarchy: Org (customer) → Research Domain (workspace).
 * All authorization (members, roles, policies, groups, settings) is scoped to RDs.
 */

function getApiBase(): string {
  return localStorage.getItem('authServiceUrl') || import.meta.env.VITE_AUTH_SERVICE_URL || ''
}

function getHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  const key = localStorage.getItem('authServiceKey')
  if (key) headers['X-Service-Key'] = key
  return headers
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${getApiBase()}${path}`, {
    ...init,
    headers: { ...getHeaders(), ...init?.headers },
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }))
    throw new ApiError((body as { error: string }).error || response.statusText, response.status)
  }

  return response.json() as Promise<T>
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

// ── Health ─────────────────────────────────────────────────────────────

export function getHealth() {
  return apiFetch<{ status: string; service: string }>('/health')
}

// ── Organizations (customer containers) ──────────────────────────────

export interface Org {
  id: string
  name: string
  slug: string
  logo: string | null
  metadata: string | null
  createdAt: string
}

export function listOrgs() {
  return apiFetch<Org[]>('/api/orgs')
}

export function getOrg(orgId: string) {
  return apiFetch<{ org: Org; rdCount: number }>(`/api/orgs/${orgId}`)
}

export function createOrg(body: { name: string; slug: string; logo?: string; metadata?: Record<string, unknown> }) {
  return apiFetch<Org>('/api/orgs', { method: 'POST', body: JSON.stringify(body) })
}

export function updateOrg(orgId: string, body: { name?: string; slug?: string; logo?: string }) {
  return apiFetch<{ updated: boolean }>(`/api/orgs/${orgId}`, { method: 'PUT', body: JSON.stringify(body) })
}

// ── Research Domains (workspaces within orgs) ────────────────────────

export interface ResearchDomain {
  id: string
  orgId: string
  name: string
  slug: string
  description: string | null
  isArchived: number
  archivedAt: string | null
  archivedBy: string | null
  createdAt: string
  memberCount: number
}

export function listRds(orgId: string, includeArchived = false) {
  const params = includeArchived ? '?includeArchived=true' : ''
  return apiFetch<ResearchDomain[]>(`/api/orgs/${orgId}/rds${params}`)
}

export function getRd(rdId: string) {
  return apiFetch<{ rd: ResearchDomain; memberCount: number }>(`/api/rds/${rdId}`)
}

export function createRd(orgId: string, body: { name: string; slug: string; description?: string; creatorEmail: string; creatorUserId?: string; creatorName?: string }) {
  return apiFetch<ResearchDomain>(`/api/orgs/${orgId}/rds`, { method: 'POST', body: JSON.stringify(body) })
}

export function updateRd(rdId: string, body: { name?: string; slug?: string; description?: string }) {
  return apiFetch<{ updated: boolean }>(`/api/rds/${rdId}`, { method: 'PUT', body: JSON.stringify(body) })
}

export function archiveRd(rdId: string) {
  return apiFetch<{ archived: boolean }>(`/api/rds/${rdId}/archive`, { method: 'POST' })
}

export function unarchiveRd(rdId: string) {
  return apiFetch<{ unarchived: boolean }>(`/api/rds/${rdId}/unarchive`, { method: 'POST' })
}

// ── Members (scoped to RD) ───────────────────────────────────────────

export interface Member {
  id: string
  userId: string
  rdId: string
  role: string
  createdAt: string
  name: string
  email: string
  cognitoSub: string | null
  emailVerified: number
}

export function listRdMembers(rdId: string) {
  return apiFetch<Member[]>(`/api/rds/${rdId}/members`)
}

export function addRdMember(rdId: string, body: { email: string; role: string; userId?: string; name?: string }) {
  return apiFetch<{ added: boolean }>(`/api/rds/${rdId}/members`, { method: 'POST', body: JSON.stringify(body) })
}

export function updateRdMemberRole(rdId: string, userId: string, body: { role: string }) {
  return apiFetch<{ updated: boolean }>(`/api/rds/${rdId}/members/${userId}`, { method: 'PUT', body: JSON.stringify(body) })
}

export function removeRdMember(rdId: string, userId: string) {
  return apiFetch<{ removed: boolean }>(`/api/rds/${rdId}/members/${userId}`, { method: 'DELETE' })
}

// ── Roles (scoped to RD) ─────────────────────────────────────────────

export interface Role {
  id: string
  rdId: string
  name: string
  description: string | null
  isDefault: number
  createdAt: string
}

export function listRoles(rdId: string) {
  return apiFetch<Role[]>(`/api/rds/${rdId}/roles`)
}

export function createRole(rdId: string, body: { name: string; description?: string; isDefault?: boolean }) {
  return apiFetch<Role>(`/api/rds/${rdId}/roles`, { method: 'POST', body: JSON.stringify(body) })
}

export function updateRole(rdId: string, roleId: string, body: { name?: string; description?: string; isDefault?: boolean }) {
  return apiFetch<{ updated: boolean }>(`/api/rds/${rdId}/roles/${roleId}`, { method: 'PUT', body: JSON.stringify(body) })
}

export function deleteRole(rdId: string, roleId: string) {
  return apiFetch<{ deleted: boolean }>(`/api/rds/${rdId}/roles/${roleId}`, { method: 'DELETE' })
}

export function seedRoles(rdId: string) {
  return apiFetch<{ seeded: boolean }>(`/api/rds/${rdId}/roles/seed`, { method: 'POST' })
}

// ── Policies (scoped to RD) ──────────────────────────────────────────

export type ActorType = 'user' | 'agent' | 'agent:trusted' | 'agent:standard' | 'agent:restricted'

export interface Policy {
  id: string
  rdId: string
  role: string
  actorType: ActorType
  resourceType: string
  action: string
  effect: 'allow' | 'deny'
  viewAll: number
  modifyAll: number
  source?: 'template' | 'override'
}

export function listPolicies(rdId: string) {
  return apiFetch<Policy[]>(`/api/rds/${rdId}/policies`)
}

export function upsertPolicy(rdId: string, body: { role: string; actorType: string; resourceType: string; action: string; effect: string }) {
  return apiFetch<Policy>(`/api/rds/${rdId}/policies`, { method: 'POST', body: JSON.stringify(body) })
}

export function bulkUpdatePolicies(rdId: string, body: { role: string; policies: Array<{ actorType: string; resourceType: string; action: string; effect: string; viewAll?: boolean; modifyAll?: boolean }> }) {
  return apiFetch<{ count: number }>(`/api/rds/${rdId}/policies/bulk`, { method: 'PUT', body: JSON.stringify(body) })
}

export function deletePolicy(rdId: string, policyId: string) {
  return apiFetch<{ deleted: boolean }>(`/api/rds/${rdId}/policies/${policyId}`, { method: 'DELETE' })
}

export function getPolicyVersion(rdId: string) {
  return apiFetch<{ rdId: string; version: number }>(`/api/rds/${rdId}/policy-version`)
}

// ── Agents (OBO registry + trust tiers, scoped to RD) ────────────────

export type AgentTier = 'trusted' | 'standard' | 'restricted'
export type AgentStatus = 'pending' | 'active' | 'rejected' | 'suspended'

export interface Agent {
  id: string
  rdId: string
  name: string
  description: string | null
  tier: AgentTier
  status: AgentStatus
  registeredBy: string | null
  reviewedBy: string | null
  reviewedAt: string | null
  rejectionReason: string | null
  createdAt: string
  updatedAt: string
}

export function listAgents(rdId: string, status?: AgentStatus) {
  const qs = status ? `?status=${status}` : ''
  return apiFetch<Agent[]>(`/api/rds/${rdId}/agents${qs}`)
}

/** Submit an agent for review — open to any caller; lands pending/restricted. */
export function submitAgent(rdId: string, body: { name: string; description?: string }) {
  return apiFetch<Agent>(`/api/rds/${rdId}/agents`, { method: 'POST', body: JSON.stringify(body) })
}

/** Approve a pending agent at a chosen tier — platform admin only. */
export function approveAgent(rdId: string, agentId: string, tier: AgentTier) {
  return apiFetch<{ approved: boolean }>(`/api/rds/${rdId}/agents/${agentId}/approve`, { method: 'POST', body: JSON.stringify({ tier }) })
}

/** Reject a pending agent with a reason — platform admin only. */
export function rejectAgent(rdId: string, agentId: string, reason: string) {
  return apiFetch<{ rejected: boolean }>(`/api/rds/${rdId}/agents/${agentId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) })
}

/** Suspend/reactivate or retier an active agent — platform admin only. */
export function updateAgent(rdId: string, agentId: string, body: { description?: string; tier?: AgentTier; status?: 'active' | 'suspended' }) {
  return apiFetch<{ updated: boolean }>(`/api/rds/${rdId}/agents/${agentId}`, { method: 'PUT', body: JSON.stringify(body) })
}

export function deleteAgent(rdId: string, agentId: string) {
  return apiFetch<{ deleted: boolean }>(`/api/rds/${rdId}/agents/${agentId}`, { method: 'DELETE' })
}

// ── Audit log (R2-backed, scoped to RD) ──────────────────────────────

export interface AuditEvent {
  seq: number
  ts: string
  rdId: string | null
  actorUserId: string
  actorType?: string
  isSystemAdmin?: boolean
  category: 'authz_decision' | 'mutation'
  resourceType?: string | null
  action?: string | null
  effect?: 'allow' | 'deny' | null
  reason?: string | null
}

export interface AuditExport {
  events: AuditEvent[]
  chainIntact: boolean
  batchesScanned: number
}

export function exportAudit(rdId: string, from?: string, to?: string) {
  const params = new URLSearchParams()
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  const qs = params.toString() ? `?${params.toString()}` : ''
  return apiFetch<AuditExport>(`/api/rds/${rdId}/audit${qs}`)
}

// ── Groups (scoped to RD) ────────────────────────────────────────────

export interface Group {
  id: string
  rdId: string
  name: string
  description: string | null
  createdAt: string
}

export interface GroupMembership {
  groupId: string
  userId: string
  addedBy: string | null
  createdAt: string
}

export function listGroups(rdId: string) {
  return apiFetch<Group[]>(`/api/rds/${rdId}/groups`)
}

export function getGroup(rdId: string, groupId: string) {
  return apiFetch<{ group: Group; members: GroupMembership[] }>(`/api/rds/${rdId}/groups/${groupId}`)
}

export function createGroup(rdId: string, body: { name: string; description?: string }) {
  return apiFetch<Group>(`/api/rds/${rdId}/groups`, { method: 'POST', body: JSON.stringify(body) })
}

export function updateGroup(rdId: string, groupId: string, body: { name?: string; description?: string }) {
  return apiFetch<{ updated: boolean }>(`/api/rds/${rdId}/groups/${groupId}`, { method: 'PUT', body: JSON.stringify(body) })
}

export function deleteGroup(rdId: string, groupId: string) {
  return apiFetch<{ deleted: boolean }>(`/api/rds/${rdId}/groups/${groupId}`, { method: 'DELETE' })
}

export function addGroupMember(rdId: string, groupId: string, body: { userId: string }) {
  return apiFetch<{ added: boolean }>(`/api/rds/${rdId}/groups/${groupId}/members`, { method: 'POST', body: JSON.stringify(body) })
}

export function removeGroupMember(rdId: string, groupId: string, userId: string) {
  return apiFetch<{ removed: boolean }>(`/api/rds/${rdId}/groups/${groupId}/members/${userId}`, { method: 'DELETE' })
}

// ── Settings (scoped to RD) ──────────────────────────────────────────

export interface SettingDefinition {
  key: string
  label: string
  description: string | null
  valueType: 'enum' | 'boolean' | 'string' | 'json'
  allowedValues: string | null
  defaultValue: string
  category: string
  sortOrder: number
}

export interface RdSetting extends SettingDefinition {
  value: string
}

export function listSettingDefinitions() {
  return apiFetch<SettingDefinition[]>('/api/settings/definitions')
}

export function createSettingDefinition(body: { key: string; label: string; description?: string; valueType: string; allowedValues?: string[]; defaultValue: string; category?: string; sortOrder?: number }) {
  return apiFetch<SettingDefinition>('/api/settings/definitions', { method: 'POST', body: JSON.stringify(body) })
}

export function updateSettingDefinition(key: string, body: { label?: string; description?: string; allowedValues?: string[]; defaultValue?: string; category?: string; sortOrder?: number }) {
  return apiFetch<{ updated: boolean }>(`/api/settings/definitions/${key}`, { method: 'PUT', body: JSON.stringify(body) })
}

export function deleteSettingDefinition(key: string) {
  return apiFetch<{ deleted: boolean }>(`/api/settings/definitions/${key}`, { method: 'DELETE' })
}

export function getRdSettings(rdId: string) {
  return apiFetch<RdSetting[]>(`/api/rds/${rdId}/settings`)
}

export function updateRdSettings(rdId: string, body: Record<string, string>) {
  return apiFetch<{ updated: number; errors: Array<{ key: string; error: string }> }>(`/api/rds/${rdId}/settings`, { method: 'PUT', body: JSON.stringify(body) })
}

// ── Bulk Import (scoped to RD) ───────────────────────────────────────

export interface BulkMemberInput {
  userId: string
  email: string
  name: string
  role: string
}

export interface BulkImportResult {
  imported: number
  skipped: number
  errors: Array<{ userId: string; error: string }>
}

export function bulkImportMembers(rdId: string, members: BulkMemberInput[]) {
  return apiFetch<BulkImportResult>(`/api/rds/${rdId}/members/bulk`, { method: 'POST', body: JSON.stringify({ members }) })
}

// ── Resource Types (platform admin registry) ────────────────────────

export interface ResourceType {
  key: string
  label: string
  description: string | null
  category: string
  sortOrder: number
}

export function listResourceTypes() {
  return apiFetch<ResourceType[]>('/api/resource-types')
}

export function createResourceType(body: { key: string; label: string; description?: string; category?: string; sortOrder?: number }) {
  return apiFetch<ResourceType>('/api/resource-types', { method: 'POST', body: JSON.stringify(body) })
}

export function updateResourceType(key: string, body: { label?: string; description?: string; category?: string; sortOrder?: number }) {
  return apiFetch<{ updated: boolean }>(`/api/resource-types/${key}`, { method: 'PUT', body: JSON.stringify(body) })
}

export function deleteResourceType(key: string) {
  return apiFetch<{ deleted: boolean }>(`/api/resource-types/${key}`, { method: 'DELETE' })
}

// ── User Context ──────────────────────────────────────────────────────

export interface UserContext {
  userId: string
  rdId: string | null
  role: string | null
  groups: string[]
  isSystemAdmin: boolean
}

export function getUserContext(userId: string) {
  return apiFetch<UserContext>(`/api/user/${userId}/context`)
}

export function syncUser(userId: string, body: { email: string; name: string }) {
  return apiFetch<{ synced: boolean }>(`/api/user/${userId}/sync`, { method: 'PUT', body: JSON.stringify(body) })
}

// ── Check ─────────────────────────────────────────────────────────────

export interface CheckResult {
  allowed: boolean
  role: string | null
  reason: string
}

export function checkPolicy(body: { userId: string; rdId: string; actorType: string; resourceType: string; action: string; agentId?: string }) {
  return apiFetch<CheckResult>('/api/check', { method: 'POST', body: JSON.stringify(body) })
}
